import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { delay, filter, map, tap } from 'rxjs/operators';

import { ColorModeService } from '@coreui/angular-pro';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset } from './icons/icon-subset';
import { EnvironmentBannerComponent } from './shared/environment-banner/environment-banner.component';
import { AppShellPresenceService } from './services/app-shell-presence.service';
import { EnvironmentService } from './services/environment.service';

@Component({
  selector: 'app-root',
  template: `
      @if (zeigeUmgebungsBanner && !shellPresence.mounted()) {
        <app-environment-banner variant="floating" />
      }
      <router-outlet />
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, EnvironmentBannerComponent],
})
export class AppComponent implements OnInit {
  title = 'PLANAUDIT';

  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);
  readonly #environment = inject(EnvironmentService);

  /** Public, weil das Template darauf zugreift. */
  readonly shellPresence = inject(AppShellPresenceService);
  /** Auf Live soll nicht einmal ein leerer Banner-Tag im DOM stehen. */
  readonly zeigeUmgebungsBanner = this.#environment.kennzeichen !== null;

  readonly #colorModeService = inject(ColorModeService);
  readonly #iconSetService = inject(IconSetService);

  constructor() {
    // Präfix macht Test-/Dev-Tabs in der Tab-Leiste unterscheidbar; auf Live leer.
    this.#titleService.setTitle(this.#environment.titelPraefix + this.title);
    // iconSet singleton
    this.#iconSetService.icons = { ...iconSubset };
    this.#colorModeService.localStorageItemName.set('planaudit-theme-default');
    this.#colorModeService.eventName.set('ColorSchemeChange');
  }

  ngOnInit(): void {
    this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
    });

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map((params) => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
        filter((theme) => ['dark', 'light', 'auto'].includes(theme)),
        tap((theme) => {
          this.#colorModeService.colorMode.set(theme);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }
}
